import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './ui/table';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  ArrowRight,
  Sparkles,
  Database,
  FileText,
  Eye,
  Download
} from 'lucide-react';

interface DataMappingVisualizationProps {
  attachment: any;
  onAcceptMapping?: (suggestion: any) => void;
  onRejectMapping?: (suggestion: any) => void;
}

export function DataMappingVisualization({
  attachment,
  onAcceptMapping,
  onRejectMapping
}: DataMappingVisualizationProps) {
  const [acceptedMappings, setAcceptedMappings] = useState<Set<string>>(new Set());
  const [rejectedMappings, setRejectedMappings] = useState<Set<string>>(new Set());

  const aiAnalysis = attachment?.aiAnalysis;

  if (!aiAnalysis) {
    return (
      <Alert>
        <AlertDescription>
          No AI analysis available for this attachment.
        </AlertDescription>
      </Alert>
    );
  }

  const handleAccept = (suggestion: any, index: number) => {
    const key = `${suggestion.sourceField}_${index}`;
    setAcceptedMappings(new Set(acceptedMappings).add(key));
    setRejectedMappings(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    
    if (onAcceptMapping) {
      onAcceptMapping(suggestion);
    }
  };

  const handleReject = (suggestion: any, index: number) => {
    const key = `${suggestion.sourceField}_${index}`;
    setRejectedMappings(new Set(rejectedMappings).add(key));
    setAcceptedMappings(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    
    if (onRejectMapping) {
      onRejectMapping(suggestion);
    }
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 80) {
      return <Badge className="bg-green-600">High: {confidence}%</Badge>;
    } else if (confidence >= 60) {
      return <Badge variant="secondary">Medium: {confidence}%</Badge>;
    } else {
      return <Badge variant="outline">Low: {confidence}%</Badge>;
    }
  };

  const getDataQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-blue-600';
      case 'fair': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-4">
      {/* Analysis Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            AI Analysis Results
          </CardTitle>
          <CardDescription>
            Detected: {aiAnalysis.detectedVendor ? 
              `${aiAnalysis.detectedVendor} export` : 
              'Unknown format'
            } ({aiAnalysis.confidence}% confidence)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm mb-4">{aiAnalysis.summary}</p>
          
          {aiAnalysis.visualization?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <div className="text-2xl font-bold">
                  {aiAnalysis.visualization.summary.totalRecords}
                </div>
                <div className="text-xs text-muted-foreground">Total Records</div>
              </div>
              
              <div>
                <div className="text-2xl font-bold">
                  {Object.keys(aiAnalysis.visualization.summary.categories || {}).length}
                </div>
                <div className="text-xs text-muted-foreground">Categories</div>
              </div>
              
              <div>
                <div className={`text-2xl font-bold ${getDataQualityColor(aiAnalysis.visualization.summary.dataQuality)}`}>
                  {aiAnalysis.visualization.summary.dataQuality}
                </div>
                <div className="text-xs text-muted-foreground">Data Quality</div>
              </div>
              
              <div>
                <div className="text-2xl font-bold">
                  {aiAnalysis.mappingSuggestions?.length || 0}
                </div>
                <div className="text-xs text-muted-foreground">Mappings Found</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs for different views */}
      <Tabs defaultValue="mappings" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="mappings">
            <ArrowRight className="h-4 w-4 mr-2" />
            Mappings ({aiAnalysis.mappingSuggestions?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="data">
            <Database className="h-4 w-4 mr-2" />
            Data Preview
          </TabsTrigger>
          <TabsTrigger value="workflow">
            <FileText className="h-4 w-4 mr-2" />
            Workflows
          </TabsTrigger>
        </TabsList>

        {/* Mapping Suggestions Tab */}
        <TabsContent value="mappings" className="space-y-4">
          {aiAnalysis.mappingSuggestions && aiAnalysis.mappingSuggestions.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Suggested Data Mappings</CardTitle>
                <CardDescription>
                  Review and accept AI-suggested mappings for your migration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {aiAnalysis.mappingSuggestions.map((suggestion: any, index: number) => {
                    const key = `${suggestion.sourceField}_${index}`;
                    const isAccepted = acceptedMappings.has(key);
                    const isRejected = rejectedMappings.has(key);

                    return (
                      <div 
                        key={index}
                        className={`
                          border rounded-lg p-4 transition-colors
                          ${isAccepted ? 'bg-green-50 dark:bg-green-950 border-green-500' : ''}
                          ${isRejected ? 'bg-red-50 dark:bg-red-950 border-red-500' : ''}
                        `}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {getConfidenceBadge(suggestion.confidence)}
                              <Badge variant="outline">{suggestion.sourceType}</Badge>
                            </div>
                            
                            <div className="flex items-center gap-3 text-sm">
                              <code className="px-2 py-1 bg-muted rounded">
                                {suggestion.sourceField}
                              </code>
                              <ArrowRight className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{suggestion.targetCI}</span>
                              <span className="text-muted-foreground">→</span>
                              <code className="px-2 py-1 bg-muted rounded">
                                {suggestion.targetField}
                              </code>
                            </div>
                          </div>

                          <div className="flex gap-2 ml-4">
                            {!isAccepted && !isRejected && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleAccept(suggestion, index)}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleReject(suggestion, index)}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            {isAccepted && (
                              <Badge className="bg-green-600">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Accepted
                              </Badge>
                            )}
                            {isRejected && (
                              <Badge variant="destructive">
                                <XCircle className="h-3 w-3 mr-1" />
                                Rejected
                              </Badge>
                            )}
                          </div>
                        </div>

                        <p className="text-sm text-muted-foreground mb-2">
                          {suggestion.reasoning}
                        </p>

                        {suggestion.sampleData && suggestion.sampleData.length > 0 && (
                          <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
                            <strong>Sample data:</strong> {suggestion.sampleData.slice(0, 3).join(', ')}
                            {suggestion.sampleData.length > 3 && ` (+${suggestion.sampleData.length - 3} more)`}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {acceptedMappings.size > 0 && (
                  <Alert className="mt-4 border-green-500 bg-green-50 dark:bg-green-950">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <AlertDescription className="text-green-800 dark:text-green-200">
                      {acceptedMappings.size} mapping{acceptedMappings.size !== 1 ? 's' : ''} accepted. 
                      These will be used during migration.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          ) : (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                No mapping suggestions available. The AI couldn't detect a clear data structure.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Data Preview Tab */}
        <TabsContent value="data" className="space-y-4">
          {aiAnalysis.visualization?.tables && aiAnalysis.visualization.tables.length > 0 ? (
            aiAnalysis.visualization.tables.map((table: any, tableIndex: number) => (
              <Card key={tableIndex}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{table.name}</span>
                    <Badge variant="outline">
                      {table.totalRows} rows
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {table.columns.map((col: string, colIndex: number) => (
                            <TableHead key={colIndex}>{col}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {table.rows.slice(0, 10).map((row: any[], rowIndex: number) => (
                          <TableRow key={rowIndex}>
                            {row.map((cell: any, cellIndex: number) => (
                              <TableCell key={cellIndex}>{cell}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {table.totalRows > 10 && (
                    <p className="text-sm text-muted-foreground mt-2 text-center">
                      Showing 10 of {table.totalRows} rows
                    </p>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Alert>
              <AlertDescription>
                No tabular data found in this file.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Workflow Tab */}
        <TabsContent value="workflow" className="space-y-4">
          {aiAnalysis.workflowData ? (
            <Card>
              <CardHeader>
                <CardTitle>Extracted Workflow</CardTitle>
                <CardDescription>
                  Statuses: {aiAnalysis.workflowData.statuses?.length || 0} | 
                  Transitions: {aiAnalysis.workflowData.transitions?.length || 0}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Statuses */}
                {aiAnalysis.workflowData.statuses && aiAnalysis.workflowData.statuses.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Statuses</h4>
                    <div className="flex flex-wrap gap-2">
                      {aiAnalysis.workflowData.statuses.map((status: any, index: number) => (
                        <Badge key={index} variant="secondary">
                          {status.name}
                          {status.type && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({status.type})
                            </span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transitions */}
                {aiAnalysis.workflowData.transitions && aiAnalysis.workflowData.transitions.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Transitions</h4>
                    <div className="space-y-2">
                      {aiAnalysis.workflowData.transitions.map((transition: any, index: number) => (
                        <div key={index} className="border rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">{transition.from}</Badge>
                            <ArrowRight className="h-4 w-4" />
                            <Badge variant="outline">{transition.to}</Badge>
                            <span className="text-sm font-medium ml-2">{transition.name}</span>
                          </div>
                          
                          {transition.validators && transition.validators.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              Validators: {transition.validators.join(', ')}
                            </div>
                          )}
                          
                          {transition.postFunctions && transition.postFunctions.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              Post-functions: {transition.postFunctions.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Workflow Diagram (if available) */}
                {aiAnalysis.workflowData.diagram && (
                  <div>
                    <h4 className="font-medium mb-2">Workflow Diagram</h4>
                    <Alert>
                      <Eye className="h-4 w-4" />
                      <AlertDescription>
                        Interactive workflow diagram will be rendered here using React Flow
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Alert>
              <AlertDescription>
                No workflow data found in this file. Upload a workflow diagram image for AI extraction.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
